import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";

import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getListProjectsQueryKey } from "~/api/folders/folders";
import {
  getGetProjectQueryKey,
  useGetProject,
  useUpdateProject,
} from "~/api/projects/projects";
import DayPicker from "~/components/DayPicker";
import { useOrganizationParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import { createCaseSchema } from "../../cases/validations/create-case";

interface EditCaseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
}

type EditCaseFormData = z.infer<ReturnType<typeof createCaseSchema>>;

export const EditCaseDialog: FC<EditCaseDialogProps> = ({
  open,
  setOpen,
  projectId,
}) => {
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { data: project } = useGetProject(organizationId, projectId);
  const [dataLoaded, setDataLoaded] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    control,
    reset,
  } = useForm<EditCaseFormData>({
    resolver: dataLoaded ? zodResolver(createCaseSchema(intl)) : undefined,
    defaultValues: {
      name: "",
      claimNumber: "",
      dateOfBirth: undefined,
      dateOfLoss: undefined,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (project) {
      reset({
        name: project.name,
        claimNumber: project.claimNumber,
        dateOfBirth: project.dateOfBirth
          ? dayjs(project.dateOfBirth).toDate()
          : undefined,
        dateOfLoss: project.dateOfLoss
          ? dayjs(project.dateOfLoss).toDate()
          : undefined,
      });
      setDataLoaded(true);
    }
  }, [project, reset]);

  const { mutate: updateProject, isPending } = useUpdateProject();
  const queryClient = useQueryClient();

  const onSubmit = handleSubmit((data) => {
    const body = {
      name: data.name,
      claimNumber: data.claimNumber,
      dateOfBirth: dayjs(data.dateOfBirth).format("YYYY-MM-DD"),
      dateOfLoss: data.dateOfLoss
        ? dayjs(data.dateOfLoss).format("YYYY-MM-DD")
        : null,
    };

    updateProject(
      {
        organizationId,
        projectId,
        data: body,
      },
      {
        onSuccess: () => {
          // Invalidate the individual project query
          queryClient.invalidateQueries({
            queryKey: getGetProjectQueryKey(organizationId, projectId),
          });
          queryClient.invalidateQueries({
            queryKey: getListProjectsQueryKey(organizationId),
          });
          setOpen(false);
        },
      }
    );
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[608px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id="editCase" />
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <>
            <div
              className={`flex flex-col gap-4 mb-4 ${!dataLoaded ? "opacity-50" : ""}`}
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="caseName">
                  <FormattedMessage id="casesList.createCaseDialog.caseNameLabel" />
                </Label>
                <Input
                  id="caseName"
                  placeholder={intl.formatMessage({
                    id: "casesList.createCaseDialog.caseNamePlaceholder",
                  })}
                  className="shadow-none"
                  {...register("name")}
                  disabled={!dataLoaded}
                />
                {errors.name && dataLoaded && (
                  <span className="text-red-500 text-sm">
                    {errors.name.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="dateOfBirth">
                  <FormattedMessage
                    id="dateOfBirthLabel"
                    defaultMessage="Date of Birth"
                  />
                </Label>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  rules={{
                    required: "Date of Birth is required",
                  }}
                  render={({ field }) => (
                    <DayPicker
                      value={field.value || null}
                      onChange={field.onChange}
                      placeholder="MM/DD/YYYY"
                      disabled={!dataLoaded}
                      minDate={new Date("1900-01-01")}
                      maxDate={new Date()}
                    />
                  )}
                />
                {errors.dateOfBirth && dataLoaded && (
                  <span className="text-red-500 text-sm">
                    {errors.dateOfBirth.message}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="dateOfLoss">
                  <FormattedMessage
                    id="dateOfLossLabel"
                    defaultMessage="Date of Loss"
                  />
                </Label>
                <Controller
                  name="dateOfLoss"
                  control={control}
                  render={({ field }) => (
                    <DayPicker
                      value={field.value || null}
                      onChange={field.onChange}
                      placeholder="MM/DD/YYYY"
                      disabled={!dataLoaded}
                      minDate={new Date("1900-01-01")}
                      maxDate={new Date()}
                    />
                  )}
                />
                {errors.dateOfLoss && dataLoaded && (
                  <span className="text-red-500 text-sm">
                    {errors.dateOfLoss.message}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="claimNumber">
                  <FormattedMessage
                    id="claimNumberLabel"
                    defaultMessage="Claim Number"
                  />
                </Label>
                <Input
                  id="claimNumber"
                  placeholder="235848593"
                  className="shadow-none"
                  {...register("claimNumber")}
                  disabled={!dataLoaded}
                />
                {errors.claimNumber && dataLoaded && (
                  <span className="text-red-500 text-sm">
                    {errors.claimNumber.message}
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  trackEvent({
                    eventName: "app/case_edit_modal/cancel.button/click",
                  });
                  setOpen(false);
                }}
              >
                <FormattedMessage id="cancel" />
              </Button>
              <Button
                disabled={isPending || !isValid || !dataLoaded}
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  trackEvent({
                    eventName: "app/case_edit_modal/save.button/submit",
                  });
                }}
              >
                <FormattedMessage id="save" />
              </Button>
            </div>
          </>
        </form>
      </DialogContent>
    </Dialog>
  );
};
